import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { toast } from 'react-toastify';
import { Plus, Edit, Trash2, Save, X, Image, Search, Link, Lock, Filter, RotateCcw, Download } from "lucide-react";
// Temporarily disable ImageUpload to debug error
// import ImageUpload from "../../ImageUpload";

// Re-enable ReactQuill for rich text editing
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (url) => {
  if (!url) return '';
  // If URL is already absolute (starts with http:// or https://), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If URL starts with /api, prepend BACKEND_URL
  if (url.startsWith('/api')) {
    return `${BACKEND_URL}${url}`;
  }
  // Otherwise, prepend API
  return `${API}${url}`;
};

// Quill editor configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false
  }
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent',
  'link'
];

const ContentTab = ({ content, fetchContent, categories, setCategories, refreshStats }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [contentCategories, setContentCategories] = useState([]);

  // Fetch content categories for filter dropdown
  useEffect(() => {
    const fetchContentCategories = async () => {
      try {
        const response = await axios.get(`${API}/admin/content-categories`);
        setContentCategories(response.data || []);
      } catch (error) {
        console.error('Error fetching content categories:', error);
      }
    };
    fetchContentCategories();
  }, []);

  const handleAddClick = () => {
    setShowAddForm(true);
  };

  // Get unique content types from content
  const contentTypes = [...new Set(content.map(item => item.content_type).filter(Boolean))];

  // Filter content based on search and filters
  const filteredContent = content.filter(item => {
    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || 
      item.title?.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.tags?.some(tag => tag.toLowerCase().includes(searchLower));

    // Category filter - compare against category title
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;

    // Type filter
    const matchesType = filterType === 'all' || item.content_type === filterType;

    // Visibility filter
    const matchesVisibility = filterVisibility === 'all' || 
      (filterVisibility === 'visible' && item.visible !== false) ||
      (filterVisibility === 'hidden' && item.visible === false);

    return matchesSearch && matchesCategory && matchesType && matchesVisibility;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setFilterCategory('all');
    setFilterType('all');
    setFilterVisibility('all');
  };

  const hasActiveFilters = searchQuery || filterCategory !== 'all' || filterType !== 'all' || filterVisibility !== 'all';

  const [exportingReport, setExportingReport] = useState(false);

  const handleExportReport = async () => {
    setExportingReport(true);
    try {
      const response = await axios.get(`${API}/admin/content-report/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'luminelle_content_report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Content report downloaded!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setExportingReport(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Classroom Content</h2>
        <div className="flex gap-2">
          <Button
            onClick={handleExportReport}
            disabled={exportingReport}
            variant="outline"
            className="border-burgundy/30 text-burgundy hover:bg-burgundy/5"
            data-testid="export-content-report-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {exportingReport ? 'Exporting...' : 'Export Report'}
          </Button>
          <Button
            onClick={handleAddClick}
            className="bg-burgundy hover:bg-burgundy/80 text-cream"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Content
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-cream/50 border border-burgundy/20 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 text-burgundy font-medium">
          <Filter className="w-4 h-4" />
          <span>Search & Filters</span>
          {hasActiveFilters && (
            <Badge className="bg-burgundy text-cream text-xs">
              {filteredContent.length} of {content.length} items
            </Badge>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search Input */}
          <div className="lg:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-burgundy/50" />
            <Input
              placeholder="Search by title, description, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-burgundy/30 focus:border-burgundy"
            />
          </div>

          {/* Category Filter */}
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="border-burgundy/30">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {contentCategories.map(cat => (
                <SelectItem key={cat.id} value={cat.title}>{cat.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Type Filter */}
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="border-burgundy/30">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {contentTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Visibility Filter */}
          <Select value={filterVisibility} onValueChange={setFilterVisibility}>
            <SelectTrigger className="border-burgundy/30">
              <SelectValue placeholder="All Visibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Visibility</SelectItem>
              <SelectItem value="visible">Visible Only</SelectItem>
              <SelectItem value="hidden">Hidden Only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {showAddForm && (
        <ContentForm
          categories={categories}
          setCategories={setCategories}
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            setShowAddForm(false);
            fetchContent();
            refreshStats();
          }}
        />
      )}

      {editingContent && (
        <ContentForm
          content={editingContent}
          categories={categories}
          setCategories={setCategories}
          onClose={() => setEditingContent(null)}
          onSuccess={() => {
            setEditingContent(null);
            fetchContent();
            refreshStats();
          }}
        />
      )}

      {/* Results */}
      {filteredContent.length === 0 ? (
        <div className="text-center py-12 text-burgundy/60">
          <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No content found</p>
          <p className="text-sm">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredContent.map((item) => (
            <ContentCard
              key={item.id}
              content={item}
              onEdit={() => setEditingContent(item)}
              onDelete={async () => {
                if (window.confirm(`Are you sure you want to delete "${item.title}"? This action cannot be undone.`)) {
                  try {
                    await axios.delete(`${BACKEND_URL}/api/content/${item.id}`);
                    toast.success("Content deleted successfully!");
                    fetchContent();
                  } catch (error) {
                    console.error('Error deleting content:', error);
                    toast.error("Failed to delete content. Please try again.");
                  }
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ContentCard = ({ content, onEdit, onDelete }) => {
  return (
    <Card className="hover:shadow-lg transition-all duration-300 bg-white border-burgundy/20">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="text-lg font-serif text-burgundy">{content.title}</CardTitle>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Badge variant="outline" className="border-gold text-gold">
                {content.content_type}
              </Badge>
              <Badge variant="outline" className="border-burgundy/30 text-burgundy/70">
                Order: {content.order || 0}
              </Badge>
            </div>
          </div>
          <div className="flex space-x-2">
            {content.url && (
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => window.open(content.url, '_blank')}
                title="View content"
              >
                <Link className="w-4 h-4" />
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onEdit}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div 
          className="text-burgundy/70 text-sm mb-4 rich-text-content"
          dangerouslySetInnerHTML={{ __html: content.description }}
        />
        <div className="flex flex-wrap gap-2">
          {content.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs bg-burgundy/10 text-burgundy">
              {tag}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Enhanced Content Form with Rich Text Editor, Images, and Image Library
const ContentForm = ({ content = null, onClose, onSuccess, categories, setCategories }) => {
  const [formData, setFormData] = useState({
    title: content?.title || '',
    description: content?.description || '',
    content_type: content?.content_type || 'article',
    content: content?.content || '',
    url: content?.url || '',
    category: content?.category || 'Hormone Health', // Default to first created category
    subcategory: content?.subcategory || '',
    tags: content?.tags?.join(', ') || '',
    image_url: content?.image_url || '',
    order: content?.order || 0,
    dig_deeper_url: content?.dig_deeper_url ?? '',
    marketplace_url: content?.marketplace_url ?? '',
    advanced_support_url: content?.advanced_support_url ?? '',
    visit_url: content?.visit_url ?? '',
    is_public: content?.is_public ?? false,
    blog_pillar: content?.blog_pillar ?? '',
    member_only: content?.member_only ?? false,
    scheduled_at: content?.scheduled_at ? new Date(content.scheduled_at).toISOString().slice(0, 16) : ''
  });
  
  const [videoSourceType, setVideoSourceType] = useState('url'); // 'url' or 'library'

  const [loading, setLoading] = useState(false);
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [imageLibrarySelectionType, setImageLibrarySelectionType] = useState('image'); // 'image' for thumbnails, 'video' for video content
  const [imageLibraryImages, setImageLibraryImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  useEffect(() => {
    if (content) {
      setFormData({
        title: content.title || '',
        description: content.description || '',
        content: content.content || '',
        content_type: content.content_type || 'article',
        category: content.category || '',
        subcategory: content.subcategory || '',
        author: content.author || '',
        url: content.url || '',
        tags: content.tags?.join(', ') || '',
        image_url: content.image_url || '',
        order: content.order || 0,
        dig_deeper_url: content.dig_deeper_url ?? '',
        marketplace_url: content.marketplace_url ?? '',
        advanced_support_url: content.advanced_support_url ?? '',
        visit_url: content.visit_url ?? '',
        is_public: content.is_public ?? false,
        blog_pillar: content.blog_pillar ?? '',
        member_only: content.member_only ?? false,
        scheduled_at: content.scheduled_at ? new Date(content.scheduled_at).toISOString().slice(0, 16) : ''
      });
    }
  }, [content]);

  // Fetch all content categories for subcategory lookup
  const [allCategories, setAllCategories] = useState([]);
  
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/admin/content-categories`);
        setAllCategories(response.data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch subcategories when category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!formData.category || !allCategories || allCategories.length === 0) {
        setSubcategories([]);
        return;
      }
      
      try {
        const category = allCategories.find(cat => cat.title === formData.category);
        if (category && category.id) {
          const response = await axios.get(`${API}/admin/content-subcategories/by-category/${category.id}`);
          setSubcategories(response.data || []);
        } else {
          setSubcategories([]);
        }
      } catch (error) {
        console.error('Error fetching subcategories:', error);
        setSubcategories([]);
      }
    };
    
    fetchSubcategories();
  }, [formData.category, allCategories]);

  // Auto-search when Unsplash modal opens
  useEffect(() => {
    if (showImageLibrary && imageLibraryImages.length === 0) {
      searchImageLibrary('', imageLibrarySelectionType); // Load filtered by selection type
    }
  }, [showImageLibrary, imageLibrarySelectionType]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        // Convert relative URL to full URL for display
        const fullImageUrl = `${API}${response.data.image_url}`;
        setFormData({...formData, image_url: fullImageUrl});
        toast.success('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading image');
    } finally {
      setUploading(false);
    }
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [contentCategories, setContentCategories] = useState([]);

  // Fetch content categories when component mounts
  useEffect(() => {
    console.log('[ContentForm] Component mounted, fetching categories...');
    fetchContentCategories();
  }, []);

  // Log when contentCategories changes
  useEffect(() => {
    console.log('[ContentForm] contentCategories updated:', contentCategories);
  }, [contentCategories]);

  const fetchContentCategories = async () => {
    try {
      console.log('[ContentForm] Fetching categories from API...');
      const response = await axios.get(`${API}/admin/content-categories`);
      console.log('[ContentForm] Categories response:', response.data);
      setContentCategories(response.data || []);
      if (response.data && response.data.length > 0) {
        console.log('[ContentForm] Loaded', response.data.length, 'categories');
      }
    } catch (error) {
      console.error('[ContentForm] Error fetching content categories:', error);
      // Fallback to default categories if API fails
      const fallbackCategories = [
        { id: '1', title: 'Wellness', visible: true },
        { id: '2', title: 'Nutrition', visible: true },
        { id: '3', title: 'Exercise', visible: true },
        { id: '4', title: 'Hormones', visible: true }
      ];
      console.log('[ContentForm] Using fallback categories:', fallbackCategories);
      setContentCategories(fallbackCategories);
    }
  };

  // Dynamically load ReactQuill CSS (React 19 compatible version)
  useEffect(() => {
    import('react-quill-new/dist/quill.snow.css');
  }, []);

  // Temporarily disabled ReactQuill configuration
  /*
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': ['#1A5E5E', '#D9B94E', '#8B2635', '#002447', '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF'] }, 
       { 'background': ['#1A5E5E', '#D9B94E', '#8B2635', '#002447', '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF'] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link', 'image'],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 
    'blockquote', 'code-block', 'link', 'image'
  ];
  */

  // Image Library Integration
  const searchImageLibrary = async (search = '', selectionType = null) => {
    try {
      console.log('Searching Image Library for:', search, 'type:', selectionType || imageLibrarySelectionType);
      
      // Use the selection type to filter - 'video' for video content, 'image' for thumbnails
      const fileTypeFilter = selectionType || imageLibrarySelectionType;
      
      const params = { public_access: "true" };
      if (search) params.search = search;
      if (fileTypeFilter) params.file_type = fileTypeFilter;
      
      const response = await axios.get(`${API}/admin/image-library`, {
        params
      });
      
      console.log('Image Library response:', response.data);
      
      // Transform the response to match the expected format
      const transformedImages = response.data.map(image => ({
        id: image.id,
        urls: {
          small: getImageUrl(image.url),
          regular: getImageUrl(image.url)
        },
        alt_description: image.description || image.name,
        name: image.name,
        description: image.description,
        tags: image.tags || [],
        file_type: image.file_type || 'image'
      }));
      
      setImageLibraryImages(transformedImages);
      
    } catch (error) {
      console.error('Image Library API error:', error);
      setImageLibraryImages([]);
      toast.error('Error loading image library');
    }
  };

  // Upload image to library
  const uploadToImageLibrary = async (file, name, description = '', tags = '') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name);
      formData.append('description', description);
      formData.append('tags', tags);

      const response = await axios.post(`${API}/admin/image-library/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success('Image uploaded to library successfully!');
        searchImageLibrary(); // Refresh the library
        return response.data.image;
      }
    } catch (error) {
      console.error('Error uploading to library:', error);
      toast.error('Error uploading image to library');
    }
  };

  const addCategory = async () => {
    const categoryTitle = newCategory.trim();
    if (!categoryTitle) return;

    // Check if category already exists
    if (contentCategories.some(cat => cat.title.toLowerCase() === categoryTitle.toLowerCase())) {
      toast.error('Category already exists');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/content-categories`, {
        title: categoryTitle,
        description: `Auto-created category: ${categoryTitle}`,
        visible: true,
        order: contentCategories.length + 1
      });

      // Refresh the categories list
      await fetchContentCategories();
      
      // Set the new category as selected
      setFormData({...formData, category: categoryTitle});
      setNewCategory('');
      setShowAddCategory(false);
      toast.success('Category created successfully!');
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error('Error creating category');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Convert Quill's bullet lists to proper UL tags before saving
    let contentToSave = formData.content;
    if (contentToSave) {
      // Create a temporary div to manipulate the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = contentToSave;
      
      // Quill puts data-list on LI elements, not on OL parent
      // Find all li[data-list="bullet"] and convert their parent OL to UL
      const bulletItems = tempDiv.querySelectorAll('li[data-list="bullet"]');
      console.log('Found bullet list items:', bulletItems.length);
      
      // Get unique parent OL elements
      const parentOLs = new Set();
      bulletItems.forEach(li => {
        if (li.parentElement && li.parentElement.tagName === 'OL') {
          parentOLs.add(li.parentElement);
        }
      });
      
      console.log('Converting OL parents to UL:', parentOLs.size);
      parentOLs.forEach(ol => {
        const ul = document.createElement('ul');
        // Move all children from OL to UL
        while (ol.firstChild) {
          const child = ol.firstChild;
          // Remove data-list attribute from LI elements
          if (child.nodeType === 1 && child.tagName === 'LI') {
            child.removeAttribute('data-list');
          }
          ul.appendChild(child);
        }
        // Replace OL with UL
        ol.parentNode.replaceChild(ul, ol);
      });
      
      contentToSave = tempDiv.innerHTML;
      console.log('Content after conversion:', contentToSave.substring(0, 500));
    }
    
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        content: contentToSave,
        tags: (formData.tags || '').split(',').map(tag => tag.trim()).filter(tag => tag),
        scheduled_at: formData.scheduled_at ? new Date(formData.scheduled_at).toISOString() : null
      };
      
      console.log('[ContentForm] Submitting data:', submitData);

      if (content) {
        console.log('[ContentForm] Updating existing content:', content.id);
        await axios.put(`${API}/content/${content.id}`, submitData);
        toast.success("Content updated successfully!");
      } else {
        console.log('[ContentForm] Creating new content');
        await axios.post(`${API}/content?created_by=admin`, submitData);
        toast.success("Content created successfully!");
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error saving content:', error);
      
      // More specific error messages
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const message = error.response.data?.detail || error.response.data?.message || 'Unknown server error';
        toast.error(`Error saving content (${status}): ${message}`);
      } else if (error.request) {
        // Network error
        toast.error("Network error - please check your connection");
      } else {
        // Other error
        toast.error(`Error saving content: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999] overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-burgundy">
            {content ? 'Edit Content' : 'Add New Content'}
          </h2>
          <Button variant="ghost" onClick={onClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Content Visibility Toggle */}
          <div className="bg-cream/30 border border-burgundy/20 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-burgundy mb-3">Content Access Level</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="access_level"
                  checked={formData.is_public}
                  onChange={() => setFormData({...formData, is_public: true, member_only: false})}
                  className="text-burgundy focus:ring-gold"
                />
                <div>
                  <div className="font-medium text-sm text-burgundy">🌍 Everyone (Public + Trial + Member)</div>
                  <div className="text-xs text-burgundy/60">Visible to everyone on /blog • Also in Classroom</div>
                </div>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="access_level"
                  checked={!formData.is_public && !formData.member_only}
                  onChange={() => setFormData({...formData, is_public: false, member_only: false})}
                  className="text-burgundy focus:ring-gold"
                />
                <div>
                  <div className="font-medium text-sm text-burgundy">🔓 Trial + Member (Default)</div>
                  <div className="text-xs text-burgundy/60">Only visible when logged in • Not on public blog</div>
                </div>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="access_level"
                  checked={!formData.is_public && formData.member_only}
                  onChange={() => setFormData({...formData, is_public: false, member_only: true})}
                  className="text-burgundy focus:ring-gold"
                />
                <div>
                  <div className="font-medium text-sm text-burgundy flex items-center gap-1">
                    <Lock className="h-3 w-3" /> Members Only
                  </div>
                  <div className="text-xs text-burgundy/60">Shows lock icon • Trial users see upgrade prompt</div>
                </div>
              </label>
            </div>
            
            {formData.is_public && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-burgundy mb-2">Blog Pillar (Required for public content)</label>
                <select
                  value={formData.blog_pillar || ""}
                  onChange={(e) => setFormData({...formData, blog_pillar: e.target.value})}
                  className="w-full px-3 py-2 border border-burgundy/30 rounded-md focus:outline-none focus:ring-2 focus:ring-gold bg-white text-burgundy"
                >
                  <option value="">Select pillar category</option>
                  <option value="radiant_body">Radiant Body</option>
                  <option value="balanced_mind">Balanced Mind</option>
                  <option value="enduring_vitality">Enduring Vitality</option>
                </select>
                <p className="text-xs text-burgundy/60 mt-1">This determines which pillar section the article appears under on the blog</p>
              </div>
            )}
          </div>

          {/* Scheduled Publishing */}
          <div>
            <label className="block text-sm font-medium text-burgundy mb-2">Scheduled Publish Date (optional)</label>
            <input
              type="datetime-local"
              value={formData.scheduled_at || ''}
              onChange={(e) => setFormData({...formData, scheduled_at: e.target.value})}
              className="w-full px-3 py-2 border border-burgundy/30 rounded-md focus:outline-none focus:ring-2 focus:ring-gold bg-white text-burgundy"
            />
            <p className="text-xs text-burgundy/60 mt-1">Leave blank to publish immediately. Set a future date to time-release this content.</p>
            {formData.scheduled_at && (
              <button
                type="button"
                onClick={() => setFormData({...formData, scheduled_at: ''})}
                className="text-xs text-red-500 hover:text-red-700 mt-1"
              >
                Clear scheduled date (publish immediately)
              </button>
            )}
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Content Type</label>
              <select
                value={formData.content_type}
                onChange={(e) => setFormData({...formData, content_type: e.target.value})}
                className="w-full px-3 py-2 border border-burgundy/30 rounded-md focus:outline-none focus:ring-2 focus:ring-gold bg-white text-burgundy"
              >
                <option value="article">Article</option>
                <option value="video">Video</option>
                <option value="document">Document</option>
                <option value="audio">Audio</option>
              </select>
            </div>
          </div>

          {/* Description with Rich Text */}
          <div>
            <label className="block text-sm font-medium text-burgundy mb-2">Description</label>
            <div className="border border-burgundy/30 rounded-lg bg-white">
              <ReactQuill
                theme="snow"
                value={formData.description || ''}
                onChange={(value) => setFormData({...formData, description: value})}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write a compelling description..."
                className="min-h-[120px]"
              />
            </div>
            <p className="text-xs text-burgundy/60 mt-2">
              This appears as the preview text. Use formatting for emphasis.
            </p>
          </div>

          {/* Featured Image */}
          <div>
            <label className="block text-sm font-medium text-burgundy mb-2">Featured Image</label>
            <div className="space-y-3">
              {formData.image_url && (
                <div className="relative">
                  <img 
                    src={formData.image_url} 
                    alt="Featured" 
                    className="w-full h-48 object-cover rounded-lg border-2 border-burgundy/20"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => setFormData({...formData, image_url: ''})}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="Enter image URL or paste Unsplash photo URL"
                  value={formData.image_url}
                  onChange={(e) => {
                    let url = e.target.value;
                    // Convert Unsplash webpage URLs to image URLs
                    if (url.includes('unsplash.com/photos/') && url.includes('-')) {
                      const parts = url.split('-');
                      const photoId = parts[parts.length - 1];
                      if (photoId && photoId.length > 8) {
                        url = `https://images.unsplash.com/${photoId}?w=800`;
                        toast.info('Converted Unsplash URL to image format');
                      }
                    }
                    setFormData({...formData, image_url: url});
                  }}
                  className="border-burgundy/30 focus:border-gold"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setImageLibrarySelectionType('image');
                    setShowImageLibrary(true);
                  }}
                  className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                >
                  <Image className="h-4 w-4 mr-2" />
                  Image Library
                </Button>
              </div>
            </div>
          </div>

          {/* Image Alt Text for SEO */}
          {formData.image_url && (
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Image Alt Text (SEO) 
                <span className="text-burgundy/60 font-normal ml-2">- Optional, defaults to title</span>
              </label>
              <Input
                type="text"
                placeholder="Describe the image for search engines and accessibility"
                value={formData.image_alt || ''}
                onChange={(e) => setFormData({...formData, image_alt: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
              />
              <p className="text-xs text-burgundy/60 mt-1">
                Auto-filled from image library. Customize for better SEO relevance to this content.
              </p>
            </div>
          )}

          {/* Rich Text Content or URL */}
          {formData.content_type === 'article' ? (
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Article Content</label>
              <div className="border border-burgundy/30 rounded-lg sticky-toolbar-quill bg-white">
                <ReactQuill
                  theme="snow"
                  value={formData.content || ''}
                  onChange={(value) => setFormData({...formData, content: value})}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your article content here..."
                  className="min-h-[400px]"
                />
              </div>
              <p className="text-xs text-burgundy/60 mt-2">
                Use the toolbar above for formatting: bold, italic, colors, lists, and more
              </p>
            </div>
          ) : formData.content_type === 'video' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-3">Video Source</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="url"
                      checked={videoSourceType === 'url'}
                      onChange={(e) => setVideoSourceType(e.target.value)}
                      className="h-4 w-4 text-burgundy focus:ring-burgundy border-burgundy/30"
                    />
                    <span className="text-sm text-burgundy">YouTube/External URL</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="library"
                      checked={videoSourceType === 'library'}
                      onChange={(e) => setVideoSourceType(e.target.value)}
                      className="h-4 w-4 text-burgundy focus:ring-burgundy border-burgundy/30"
                    />
                    <span className="text-sm text-burgundy">Choose from Video Library</span>
                  </label>
                </div>
              </div>
              
              {videoSourceType === 'url' ? (
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Video URL</label>
                  <Input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({...formData, url: e.target.value})}
                    className="border-burgundy/30 focus:border-gold"
                    placeholder="Enter YouTube or video URL"
                  />
                  <p className="text-xs text-burgundy/60 mt-1">Paste YouTube, Vimeo, or direct video URL</p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">Video from Library</label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={formData.url}
                      readOnly
                      className="border-burgundy/30 bg-gray-50"
                      placeholder="No video selected"
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        setImageLibrarySelectionType('video');
                        setShowImageLibrary(true);
                      }}
                      className="bg-burgundy hover:bg-burgundy/90 text-cream whitespace-nowrap"
                    >
                      Choose Video
                    </Button>
                  </div>
                  {formData.url && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                      ✓ Video selected from library
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : formData.content_type === 'audio' ? (
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Audio URL</label>
              <Input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                placeholder="Enter audio URL (podcast, mp3, etc.)"
              />
              <p className="text-xs text-burgundy/60 mt-1">Paste a direct audio file URL or streaming link</p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Document URL</label>
              <Input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                placeholder="Enter document URL"
              />
            </div>
          )}

          {/* Category, Subcategory and Tags */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Category {contentCategories.length > 0 && `(${contentCategories.length} available)`}
              </label>
              <div className="flex gap-2">
                <select
                  value={formData.category}
                  onChange={(e) => {
                    console.log('[ContentForm] Category selected:', e.target.value);
                    setFormData({...formData, category: e.target.value, subcategory: ''});
                  }}
                  className="flex-1 px-3 py-2 border border-burgundy/30 rounded-md focus:outline-none focus:ring-2 focus:ring-gold"
                >
                  <option value="">Select category</option>
                  {contentCategories && contentCategories.length > 0 ? (
                    contentCategories.map(cat => (
                      <option key={cat.id || cat.title} value={cat.title}>{cat.title}</option>
                    ))
                  ) : (
                    <option value="" disabled>Loading categories...</option>
                  )}
                </select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddCategory(true)}
                  className="border-gold text-gold hover:bg-gold/10"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {showAddCategory && (
                <div className="mt-2 flex gap-2">
                  <Input
                    placeholder="New category name"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="border-burgundy/30 focus:border-gold"
                    size="sm"
                  />
                  <Button type="button" size="sm" onClick={addCategory} className="bg-gold hover:bg-gold/80 text-burgundy">
                    Add
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddCategory(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">Tags (comma separated)</label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                className="border-burgundy/30 focus:border-gold"
                placeholder="wellness, health, tips"
              />
            </div>
          </div>

          {/* Subcategory (Optional) */}
          {formData.category && (
            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Subcategory (Optional)
              </label>
              <select
                value={formData.subcategory || ''}
                onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                className="w-full px-3 py-2 border border-burgundy/30 rounded-md focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <option value="">No Subcategory</option>
                {subcategories.map(sub => (
                  <option key={sub.id} value={sub.name}>{sub.name}</option>
                ))}
              </select>
              <p className="text-xs text-burgundy/60 mt-2">
                {subcategories.length > 0 
                  ? 'Select a subcategory to organize this content within the category'
                  : `No subcategories set up for "${formData.category}". Add them in the Categories tab.`
                }
              </p>
            </div>
          )}

          {/* Display Order */}
          <div>
            <label className="block text-sm font-medium text-burgundy mb-2">Display Order</label>
            <Input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
              className="border-burgundy/30 focus:border-gold max-w-xs"
              placeholder="0"
              min="0"
            />
            <p className="text-xs text-burgundy/60 mt-2">
              Lower numbers appear first within the category. Default is 0.
            </p>
          </div>

          {/* Optional Action Buttons */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-md font-semibold text-burgundy mb-4">Optional Action Buttons (Members Only)</h3>
            <p className="text-sm text-burgundy/60 mb-4">These buttons appear only to logged-in members, not to public blog visitors</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Dig Deeper URL (Optional)</label>
                <Input
                  type="url"
                  value={formData.dig_deeper_url}
                  onChange={(e) => setFormData({...formData, dig_deeper_url: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="https://example.com/resource"
                />
                <p className="text-xs text-burgundy/60 mt-1">
                  Add a link for users to explore more about this topic
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Marketplace URL (Optional)</label>
                <Input
                  type="url"
                  value={formData.marketplace_url}
                  onChange={(e) => setFormData({...formData, marketplace_url: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="https://example.com/product"
                />
                <p className="text-xs text-burgundy/60 mt-1">
                  Link to related marketplace products
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Advanced Support URL (Optional)</label>
                <Input
                  type="url"
                  value={formData.advanced_support_url}
                  onChange={(e) => setFormData({...formData, advanced_support_url: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="https://example.com/support"
                />
                <p className="text-xs text-burgundy/60 mt-1">
                  Link to community, coaching, or expert support
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">Visit URL (Optional)</label>
                <Input
                  type="url"
                  value={formData.visit_url}
                  onChange={(e) => setFormData({...formData, visit_url: e.target.value})}
                  className="border-burgundy/30 focus:border-gold"
                  placeholder="https://example.com/external-link"
                />
                <p className="text-xs text-burgundy/60 mt-1">
                  External link for users to visit (opens in new tab)
                </p>
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-burgundy hover:bg-burgundy/80 text-cream"
            >
              {loading ? 'Saving...' : (content ? 'Update Content' : 'Create Content')}
            </Button>
          </div>
        </form>

        {/* Image Library Modal */}
        {showImageLibrary && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[10000]">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-auto p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-xl font-bold text-burgundy">
                    {imageLibrarySelectionType === 'video' ? 'Video Library' : 'Image Library'}
                  </h3>
                  <p className="text-sm text-burgundy/60">
                    {imageLibrarySelectionType === 'video' 
                      ? 'Upload new videos or select from existing ones' 
                      : 'Upload new images or select from existing ones'}
                  </p>
                </div>
                <Button variant="ghost" onClick={() => setShowImageLibrary(false)} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                {/* Upload Section */}
                <div className="bg-burgundy/5 p-4 rounded-lg border border-burgundy/20">
                  <h3 className="font-medium text-burgundy mb-2">
                    {imageLibrarySelectionType === 'video' ? 'Upload New Video' : 'Upload New Image'}
                  </h3>
                  <div className="flex gap-2">
                    <Input
                      type="file"
                      accept={imageLibrarySelectionType === 'video' ? 'video/*' : 'image/*'}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          await uploadToImageLibrary(file, file.name, '', 'wellness,content');
                        }
                      }}
                      className="border-burgundy/30 focus:border-gold"
                    />
                    <Button 
                      variant="outline"
                      className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                      onClick={() => searchImageLibrary()}
                    >
                      Refresh
                    </Button>
                  </div>
                </div>

                {/* Search Section */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Search images in your library..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="border-burgundy/30 focus:border-gold"
                  />
                  <Button onClick={() => searchImageLibrary(searchQuery)} className="bg-burgundy hover:bg-burgundy/80 text-cream">
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                  {imageLibraryImages.map((image) => (
                    <div
                      key={image.id}
                      className="cursor-pointer rounded-lg overflow-hidden border-2 border-transparent hover:border-gold transition-all"
                      onClick={() => {
                        const mediaUrl = image.urls.regular;
                        const mediaAlt = image.description || image.alt_description || image.name;
                        
                        // If selecting video for video content, set url field
                        if (imageLibrarySelectionType === 'video') {
                          setFormData({...formData, url: mediaUrl});
                          toast.success('Video selected from library!');
                        } else {
                          // Otherwise set image_url (for images)
                          setFormData({...formData, image_url: mediaUrl, image_alt: mediaAlt});
                          toast.success('Image selected from library!');
                        }
                        setShowImageLibrary(false);
                      }}
                    >
                      {image.file_type === 'video' ? (
                        <div className="w-full h-32 bg-burgundy/10 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-4xl mb-1">🎬</div>
                            <p className="text-xs text-burgundy font-medium">Video</p>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={image.urls.small}
                          alt={image.alt_description || 'Image'}
                          className="w-full h-32 object-cover"
                        />
                      )}
                      <div className="p-2">
                        <p className="text-xs text-burgundy/70 truncate">{image.alt_description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {imageLibraryImages.length === 0 && (
                  <div className="text-center py-8 text-burgundy/60">
                    <Image className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p>No images in your library yet. Upload your first image above!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentTab;
